import { useState } from 'react';

import type { VenueDetail } from '../../../../api/venues';

import { buildSummaryPatch, seedSummaryDraft, type SummaryDraft } from './summary-form';
import { useFocusReturn } from './use-focus-return';
import { useUpdateVenueMarketing } from './use-venue-marketing';
import { fieldErrorsFor, marketingErrorMessage } from './venue-marketing-errors';

/** State and save for the partner summary's inline editor. */
export function useSummaryEditor(venue: VenueDetail) {
  const update = useUpdateVenueMarketing(venue.id);
  const [editing, setEditing] = useState(false);
  // What the form held when editing began. The save is worked out against this,
  // so a field the person never touched is not sent back over someone else's change.
  const [baseline, setBaseline] = useState<SummaryDraft>(() => seedSummaryDraft(venue));
  const [draft, setDraft] = useState<SummaryDraft>(baseline);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const editButton = useFocusReturn();

  const close = () => {
    setEditing(false);
    editButton.request();
  };

  const start = () => {
    const seed = seedSummaryDraft(venue);
    setBaseline(seed);
    setDraft(seed);
    setErrors({});
    setMessage(null);
    setEditing(true);
  };

  const change = (next: SummaryDraft) => {
    setDraft(next);
    setErrors({});
    setMessage(null);
  };

  const save = async () => {
    const patch = buildSummaryPatch(draft, baseline);
    if (Object.keys(patch).length === 0) {
      close();
      return;
    }
    try {
      await update.mutateAsync(patch);
      close();
    } catch (err) {
      const fields = fieldErrorsFor(err);
      setErrors(fields);
      setMessage(
        fields._ ?? marketingErrorMessage(err, 'save-summary', venue.region?.name ?? null),
      );
    }
  };

  return {
    editing,
    draft,
    change,
    errors,
    message,
    saving: update.isPending,
    start,
    save,
    cancel: close,
    editButtonRef: editButton.ref,
  };
}
